/**
 * StepFrameworkPreview — shows the two-tab framework preview
 * (Behaviors + Competencies) for the selected employee (step 3).
 *
 * Left panel: rendered by OnboardingShell wide.
 * Right panel: scrollable framework content.
 */
import { useState } from 'react';
import {
  C, BEHAVIORS, FINN_VALUES, ALL_LEVELS, LEADERSHIP_VALUES,
  getBehaviorGroup, getCompetencies, getCompetencyDescription, getLeadershipValueGroup, getTrack,
} from '../../../data/managerReviewData';
import type { Employee } from '../../../types/managerReviewV2';
import OnboardingShell from './OnboardingShell';

const renderBold = (text: string): React.ReactNode => {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
};

const parseSentences = (text: string | null | undefined): string[] => {
  if (!text) return [];
  if (text.includes('\n\n')) return text.split('\n\n').map(s => s.trim()).filter(Boolean);
  if (text.includes('\n')) return text.split('\n').map(s => s.trim()).filter(Boolean);
  return text.split(/\. (?=[A-Z])/).map((s, i, a) => i < a.length - 1 ? s + '.' : s).filter(Boolean);
};

function SectionHeader({ label, accent }: { label: string; accent?: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
        color: accent || '#999', borderBottom: `2px solid ${accent || '#e0e0e0'}`, paddingBottom: 3,
      }}>{label}</span>
    </div>
  );
}

interface StepFrameworkPreviewProps {
  onNext: () => void;
  onBack: () => void;
  employee: Employee;
}

export default function StepFrameworkPreview({ onNext, onBack, employee }: StepFrameworkPreviewProps) {
  const [showNext, setShowNext] = useState(false);
  const [ic4Track, setIc4Track] = useState<'E' | 'M'>('E');
  const level = employee.level;
  const isM = getTrack(level) === 'M';
  const isIC4 = level === 'IC4';

  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const nextLevel = isIC4
    ? (ic4Track === 'E' ? 'E5' : 'M5')
    : (levelIdx >= 0 && levelIdx < ALL_LEVELS.length - 1 ? ALL_LEVELS[levelIdx + 1] : null);
  const nextIsM = nextLevel ? getTrack(nextLevel) === 'M' : false;
  const nextLevelLabel = isIC4 ? (ic4Track === 'E' ? 'E5' : 'M5') : nextLevel;

  const competencies = getCompetencies(employee.role, level);
  const behaviorGroup = getBehaviorGroup(level);
  const behaviorData = BEHAVIORS[behaviorGroup] || BEHAVIORS['IC3'];
  const nextBehaviorGroup = nextLevel ? getBehaviorGroup(nextLevel) : null;
  const nextBehaviorData = nextBehaviorGroup && nextBehaviorGroup !== behaviorGroup
    ? BEHAVIORS[nextBehaviorGroup] : null;

  const firstName = employee.name.split(' ')[0];

  const nextBadge = (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 700, color: C.blue,
      background: C.blue + '15', borderRadius: 4, padding: '2px 7px',
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {nextLevelLabel}
    </span>
  );

  const ValueCard = ({ v }: { v: typeof FINN_VALUES[0] }) => {
    const bullets = behaviorData[v.name] || [];
    const nextBullets = showNext && nextBehaviorData ? (nextBehaviorData[v.name] || []) : [];
    return (
      <div style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 10, padding: '16px 18px' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f0f0f', marginBottom: bullets.length ? 10 : 0 }}>{v.name}</div>
        {bullets.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5, listStyleType: 'disc' }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ fontSize: 13, color: '#444', lineHeight: 1.65 }}>{renderBold(b)}</li>
            ))}
          </ul>
        )}
        {nextBullets.length > 0 && (
          <div style={{ borderTop: '1px dashed #e4e4e4', paddingTop: 10, marginTop: 10 }}>
            <div style={{ marginBottom: 7 }}>{nextBadge}</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5, listStyleType: 'disc' }}>
              {nextBullets.map((b, i) => (
                <li key={i} style={{ fontSize: 13, color: C.blue + 'bb', lineHeight: 1.65 }}>{renderBold(b)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const CompCard = ({
    name, desc, nextDesc, isRemoved, isAdded,
  }: { name: string; desc: string | null; nextDesc?: string | null; isRemoved?: boolean; isAdded?: boolean }) => {
    const sentences = parseSentences(desc);
    const nextSentences = parseSentences(nextDesc);
    return (
      <div style={{
        background: '#fff',
        border: `1px solid ${isRemoved ? '#f5c6cb' : '#e4e4e4'}`,
        borderRadius: 10, padding: '16px 18px', opacity: isRemoved ? 0.75 : 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sentences.length ? 10 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f0f0f' }}>{name}</div>
          {isAdded && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: '#26bd59', background: '#26bd5915',
              borderRadius: 4, padding: '2px 7px', letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0,
            }}>New at {nextLevelLabel}</span>
          )}
        </div>
        {sentences.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5, listStyleType: 'disc' }}>
            {sentences.map((s, i) => (
              <li key={i} style={{ fontSize: 13, color: '#444', lineHeight: 1.65 }}>{renderBold(s)}</li>
            ))}
          </ul>
        )}
        {nextSentences.length > 0 && (
          <div style={{ borderTop: '1px dashed #e4e4e4', paddingTop: 10, marginTop: 10 }}>
            <div style={{ marginBottom: 7 }}>{nextBadge}</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5, listStyleType: 'disc' }}>
              {nextSentences.map((s, i) => (
                <li key={i} style={{ fontSize: 13, color: C.blue + 'bb', lineHeight: 1.65 }}>{renderBold(s)}</li>
              ))}
            </ul>
          </div>
        )}
        {isRemoved && (
          <div style={{ borderTop: '1px dashed #f5c6cb', paddingTop: 8, marginTop: sentences.length ? 10 : 0 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#e8294a' }}>Not required at {nextLevelLabel}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <OnboardingShell
      leftTitle={`${firstName}'s Performance Expectations in the Talent Framework`}
      wide
    >
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 4px', color: '#0f0f0f', letterSpacing: '-0.02em' }}>
            {firstName}'s Framework Preview
          </h2>
          <p style={{ color: '#aaa', fontSize: 12, margin: 0 }}>{employee.role} · {level}</p>
          <p style={{ color: '#ccc', fontSize: 11, margin: '4px 0 0', fontStyle: 'italic' }}>This view is also available as a sidebar panel during your assessment.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {isIC4 && showNext && (['E', 'M'] as const).map(t => (
            <button key={t} onClick={() => setIc4Track(t)} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontFamily: 'Inter',
              border: `1.5px solid ${ic4Track === t ? C.blue : '#ddd'}`,
              background: ic4Track === t ? C.lightBlue : '#fff',
              color: ic4Track === t ? C.blue : '#999', fontWeight: ic4Track === t ? 700 : 400,
            }}>
              {t === 'E' ? 'E5 Expert' : 'M5 Manager'}
            </button>
          ))}
          {nextLevel && (
            <button onClick={() => setShowNext(n => !n)} style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
              border: `1.5px solid ${showNext ? C.blue : '#ddd'}`,
              background: showNext ? C.lightBlue : '#fff',
              color: showNext ? C.blue : '#777',
            }}>
              {showNext ? '✕ Hide' : '＋ Show'} {nextLevelLabel}
            </button>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Competencies */}
        <div style={{ background: '#f8f8f8', border: '1px solid #e4e4e4', borderRadius: 14, padding: '22px 24px', flexShrink: 0 }}>
          <SectionHeader label="Competencies" accent="#bbb" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {competencies === null ? (
              <div style={{
                padding: '10px 14px',
                background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.35)',
                borderRadius: 7, fontSize: 13, color: '#b91c2e', lineHeight: 1.5,
              }}>
                {/* Name, never an email address: this bundle is publicly downloadable. */}
                ⚠️ Role <strong>"{employee.role}"</strong> is not in the competency database.
                Please contact <strong>Anna Kaiser</strong> (People team) to get this role added.
              </div>
            ) : null}
            {(() => {
              const currentComps = competencies || [];
              const nextComps = showNext && nextLevel ? (getCompetencies(employee.role, nextLevel) || []) : currentComps;
              const staying = currentComps.filter(c => nextComps.includes(c));
              const removed = showNext ? currentComps.filter(c => !nextComps.includes(c)) : [];
              const added = showNext ? nextComps.filter(c => !currentComps.includes(c)) : [];
              return [
                ...staying.map(c => {
                  const desc = getCompetencyDescription(c, level, employee.role);
                  const nextDesc = showNext && nextLevel ? getCompetencyDescription(c, nextLevel, employee.role) : null;
                  return <CompCard key={c} name={c} desc={desc} nextDesc={nextDesc} />;
                }),
                ...removed.map(c => (
                  <CompCard key={c} name={c} desc={getCompetencyDescription(c, level, employee.role)} isRemoved />
                )),
                ...added.map(c => (
                  <CompCard key={c} name={c} desc={null} nextDesc={nextLevel ? getCompetencyDescription(c, nextLevel, employee.role) : null} isAdded />
                )),
              ];
            })()}
            {showNext && nextIsM && !isM && Object.entries(LEADERSHIP_VALUES[getLeadershipValueGroup(nextLevel!)] || {}).map(([name, desc]) => (
              <CompCard key={name} name={name} desc={null} nextDesc={desc} isAdded />
            ))}
            {isM && Object.entries(LEADERSHIP_VALUES[getLeadershipValueGroup(level)] || {}).map(([name, desc]) => {
              const nextLVGroup = nextLevel ? getLeadershipValueGroup(nextLevel) : null;
              const nextDesc = showNext && nextLVGroup && nextLVGroup !== getLeadershipValueGroup(level)
                ? (LEADERSHIP_VALUES[nextLVGroup] || {})[name] : null;
              return <CompCard key={name} name={name} desc={desc} nextDesc={nextDesc} />;
            })}
          </div>
        </div>

        {/* Behaviors */}
        <div style={{ background: '#f8f8f8', border: '1px solid #e4e4e4', borderRadius: 14, padding: '22px 24px', flexShrink: 0 }}>
          <SectionHeader label="Behaviors" accent="#bbb" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FINN_VALUES.map(v => <ValueCard key={v.name} v={v} />)}
          </div>
        </div>

        <div style={{ height: 8 }} />
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, marginTop: 24, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{
            fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
            border: '1.5px solid #e5e7eb', cursor: 'pointer', borderRadius: 8,
            padding: '12px 20px', background: 'transparent', color: '#1a1a1a',
          }}
        >← Back</button>
        <button
          onClick={onNext}
          style={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: 15,
            border: 'none', cursor: 'pointer', borderRadius: 8,
            padding: '14px 20px', background: C.blue, color: '#fff',
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >Next →</button>
      </div>
    </OnboardingShell>
  );
}
